<template>
  <div class="reset-password-container">
    <div class="reset-password-card">
      <!-- Header Section -->
      <div class="header-section">
        <div class="logo-icon">🔑</div>
        <h1>{{ resetSuccess ? 'Mot de passe modifié' : 'Nouveau mot de passe' }}</h1>
        <p class="subtitle">
          {{ resetSuccess ? 'Votre mot de passe a été réinitialisé avec succès' : 'Choisissez un nouveau mot de passe sécurisé' }}
        </p>
      </div>

      <!-- Success State -->
      <div v-if="resetSuccess" class="success-state">
        <div class="success-icon-container">
          <div class="success-checkmark">✓</div>
        </div>
        <p class="success-message">
          Votre mot de passe a été changé avec succès.<br>
          Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
        </p>
        <p class="close-instruction">Fermez cet onglet et retournez à la page de connexion</p>
      </div>

      <!-- Form State -->
      <div v-else>
        <!-- Error Message -->
        <div v-if="errorMessage" class="error-banner">
          <span class="error-icon">⚠</span>
          <span>{{ errorMessage }}</span>
        </div>

        <!-- Invalid Token Message -->
        <div v-if="tokenInvalid" class="error-state">
          <div class="error-icon-container">
            <div class="error-x">✗</div>
          </div>
          <p class="error-text">
            Le lien de réinitialisation est invalide ou a expiré.
          </p>
          <button @click="goToForgotPassword" class="btn btn-primary">
            Demander un nouveau lien
          </button>
        </div>

        <!-- Form -->
        <form v-else @submit.prevent="handleSubmit" class="reset-password-form">
          <div class="form-group">
            <label for="password">Nouveau mot de passe *</label>
            <div class="password-input-wrapper">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Minimum 8 caractères"
                required
                :disabled="loading"
              />
              <button
                type="button"
                class="toggle-password"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                {{ showPassword ? '👁️' : '👁️‍🗨️' }}
              </button>
            </div>
            <div class="password-strength">
              <div
                class="strength-bar"
                :class="passwordStrengthClass"
                :style="{ width: passwordStrength + '%' }"
              ></div>
            </div>
            <span class="password-hint">{{ passwordStrengthText }}</span>
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirmer le mot de passe *</label>
            <div class="password-input-wrapper">
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Retapez votre mot de passe"
                required
                :disabled="loading"
              />
              <button
                type="button"
                class="toggle-password"
                @click="showConfirmPassword = !showConfirmPassword"
                tabindex="-1"
              >
                {{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}
              </button>
            </div>
            <span v-if="passwordMismatch" class="password-error">
              ⚠ Les mots de passe ne correspondent pas
            </span>
          </div>

          <div class="password-requirements">
            <div class="requirements-grid">
              <div class="requirement" :class="{ met: passwordRequirements.length }">
                <span class="requirement-icon">{{ passwordRequirements.length ? '✓' : '○' }}</span>
                <span>8+ caractères</span>
              </div>
              <div class="requirement" :class="{ met: passwordRequirements.uppercase }">
                <span class="requirement-icon">{{ passwordRequirements.uppercase ? '✓' : '○' }}</span>
                <span>Majuscule</span>
              </div>
              <div class="requirement" :class="{ met: passwordRequirements.lowercase }">
                <span class="requirement-icon">{{ passwordRequirements.lowercase ? '✓' : '○' }}</span>
                <span>Minuscule</span>
              </div>
              <div class="requirement" :class="{ met: passwordRequirements.number }">
                <span class="requirement-icon">{{ passwordRequirements.number ? '✓' : '○' }}</span>
                <span>Chiffre</span>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <button type="submit" class="btn btn-primary" :disabled="!isPasswordValid || loading">
            <span v-if="loading">Modification en cours...</span>
            <span v-else>Réinitialiser le mot de passe</span>
          </button>
        </form>
      </div>

      <!-- Footer Info -->
      <div class="footer-info">
        <p>
          En cas de problème, contactez le support.
        </p>
      </div>
    </div>

    <!-- Background decoration -->
    <div class="background-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService'

export default {
  name: 'ResetPassword',
  data() {
    return {
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      resetSuccess: false,
      tokenInvalid: false,
      errorMessage: '',
      token: '',
    }
  },
  computed: {
    passwordRequirements() {
      const password = this.password
      return {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
      }
    },
    passwordStrength() {
      const requirements = this.passwordRequirements
      const metCount = Object.values(requirements).filter(Boolean).length
      return (metCount / 4) * 100
    },
    passwordStrengthClass() {
      const strength = this.passwordStrength
      if (strength < 50) return 'weak'
      if (strength < 75) return 'medium'
      return 'strong'
    },
    passwordStrengthText() {
      const strength = this.passwordStrength
      if (strength === 0) return ''
      if (strength < 50) return 'Faible'
      if (strength < 75) return 'Moyen'
      if (strength < 100) return 'Bon'
      return 'Excellent'
    },
    passwordMismatch() {
      return (
        this.confirmPassword !== '' &&
        this.password !== this.confirmPassword
      )
    },
    isPasswordValid() {
      return (
        Object.values(this.passwordRequirements).every(Boolean) &&
        this.password === this.confirmPassword &&
        this.confirmPassword !== ''
      )
    },
  },
  mounted() {
    // Récupérer le token depuis l'URL
    this.token = this.$route.query.token

    if (!this.token) {
      this.tokenInvalid = true
    }
  },
  methods: {
    async handleSubmit() {
      if (!this.isPasswordValid || this.loading) return

      this.loading = true
      this.errorMessage = ''

      try {
        // Appeler l'API de réinitialisation de mot de passe
        await AuthService.resetPassword(this.password, this.token)

        // Afficher l'état de succès
        this.resetSuccess = true
      } catch (error) {
        console.error('Erreur lors de la réinitialisation:', error)

        // Gérer les erreurs
        if (error.response?.status === 400 || error.response?.status === 404) {
          this.tokenInvalid = true
        } else if (error.response?.data?.detail) {
          if (typeof error.response.data.detail === 'string') {
            this.errorMessage = error.response.data.detail
          } else {
            this.errorMessage = 'Une erreur est survenue. Veuillez réessayer.'
          }
        } else {
          this.errorMessage = 'Une erreur est survenue. Veuillez réessayer.'
        }
      } finally {
        this.loading = false
      }
    },

    goToLogin() {
      this.$router.push({ name: 'AccompanistLogin' })
    },

    goToForgotPassword() {
      this.$router.push({ name: 'ForgotPassword' })
    },
  },
}
</script>

<style scoped>
/* Container principal */
.reset-password-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  position: relative;
  overflow: hidden;
}

/* Background decoration */
.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 20s infinite ease-in-out;
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -150px;
  left: -150px;
  animation-delay: 0s;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: -100px;
  right: -100px;
  animation-delay: 5s;
}

.circle-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  right: -75px;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -30px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

/* Card */
.reset-password-card {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  position: relative;
  z-index: 1;
  animation: slideUp 0.6s ease;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header Section */
.header-section {
  text-align: center;
  margin-bottom: 24px;
}

.logo-icon {
  font-size: 52px;
  margin-bottom: 12px;
  animation: rotate 3s infinite;
}

@keyframes rotate {
  0%, 100% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(10deg);
  }
}

.header-section h1 {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 6px;
}

.subtitle {
  font-size: 14px;
  color: #546e7a;
  margin: 0;
  line-height: 1.5;
}

/* Error Banner */
.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border-left: 3px solid #f44336;
  border-radius: 8px;
  color: #c62828;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 16px;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.error-icon {
  font-size: 18px;
}

/* Form */
.reset-password-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-left: 4px;
}

.password-input-wrapper {
  position: relative;
}

.password-input-wrapper input {
  width: 100%;
  padding: 12px 50px 12px 14px;
  border: 2px solid #e0e7ee;
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fafbfc;
}

.password-input-wrapper input:focus {
  outline: none;
  border-color: #FF9800;
  background-color: white;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.1);
}

.password-input-wrapper input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.toggle-password:hover {
  opacity: 1;
}

/* Password Strength */
.password-strength {
  width: 100%;
  height: 6px;
  background-color: #d0d7de;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 6px;
}

.strength-bar {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 3px;
}

.strength-bar.weak {
  background: linear-gradient(90deg, #f44336, #e57373);
}

.strength-bar.medium {
  background: linear-gradient(90deg, #FF9800, #FF9800);
}

.strength-bar.strong {
  background: linear-gradient(90deg, #FF9800, #FF9800);
}

.password-hint {
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  display: block;
}

.password-hint::before {
  content: '🔒 Force: ';
}

.strength-bar.weak + .password-hint {
  color: #f44336;
}

.strength-bar.medium + .password-hint {
  color: #FF9800;
}

.strength-bar.strong + .password-hint {
  color: #FF9800;
}

.password-error {
  font-size: 12px;
  color: #f44336;
  font-weight: 600;
  margin-top: 4px;
  display: block;
}

/* Password Requirements */
.password-requirements {
  margin-top: 8px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #fff5f0 0%, #ffe8e0 100%);
  border-radius: 10px;
  border-left: 3px solid #FF9800;
}

.requirements-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 12px;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #546e7a;
  transition: all 0.3s ease;
}

.requirement.met {
  color: #FF9800;
  font-weight: 600;
}

.requirement-icon {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  border-radius: 50%;
  background: #e0e0e0;
  color: #9e9e9e;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.requirement.met .requirement-icon {
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  color: white;
  box-shadow: 0 2px 6px rgba(255, 152, 0, 0.3);
  transform: scale(1.05);
}

/* Buttons */
.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #FF9800;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: #FF9800;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
}

.btn-icon {
  font-size: 18px;
}

/* Success State */
.success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.success-icon-container {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #FF9800 0%, #FF9800 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-checkmark {
  font-size: 48px;
  color: white;
  font-weight: bold;
}

.success-message {
  text-align: center;
  font-size: 15px;
  color: #2c3e50;
  line-height: 1.6;
  margin: 0;
}

.close-instruction {
  font-weight: 600;
  color: #FF9800;
  margin-top: 20px;
  font-size: 16px;
  text-align: center;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.error-icon-container {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f44336 0%, #e57373 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s ease;
}

.error-x {
  font-size: 48px;
  color: white;
  font-weight: bold;
}

.error-text {
  text-align: center;
  font-size: 15px;
  color: #2c3e50;
  line-height: 1.6;
  margin: 0;
}

/* Footer Info */
.footer-info {
  margin-top: 20px;
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #e0e7ee;
}

.footer-info p {
  font-size: 12px;
  color: #90a4ae;
  line-height: 1.5;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .reset-password-card {
    padding: 24px 20px;
  }

  .header-section h1 {
    font-size: 22px;
  }

  .logo-icon {
    font-size: 48px;
  }

  .btn {
    width: 100%;
  }

  .requirements-grid {
    grid-template-columns: 1fr;
  }
}

/* Media query pour les écrans avec hauteur réduite */
@media (max-height: 700px) {
  .reset-password-card {
    padding: 20px;
    max-height: calc(100vh - 30px);
  }

  .header-section {
    margin-bottom: 16px;
  }

  .logo-icon {
    font-size: 44px;
    margin-bottom: 8px;
  }

  .header-section h1 {
    font-size: 22px;
    margin-bottom: 4px;
  }

  .subtitle {
    font-size: 13px;
  }

  .reset-password-form {
    gap: 14px;
    margin-bottom: 16px;
  }

  .password-input-wrapper input {
    padding: 10px 45px 10px 12px;
    font-size: 13px;
  }

  .btn {
    padding: 10px 18px;
    font-size: 14px;
  }

  .success-icon-container,
  .error-icon-container {
    width: 70px;
    height: 70px;
  }

  .success-checkmark,
  .error-x {
    font-size: 42px;
  }

  .password-requirements {
    padding: 10px 12px;
  }
}

/* Media query pour les très petits écrans en hauteur */
@media (max-height: 600px) {
  .reset-password-container {
    padding: 5px;
  }

  .reset-password-card {
    padding: 16px;
    max-height: calc(100vh - 20px);
    border-radius: 16px;
  }

  .header-section {
    margin-bottom: 12px;
  }

  .logo-icon {
    font-size: 36px;
    margin-bottom: 6px;
  }

  .header-section h1 {
    font-size: 20px;
    margin-bottom: 3px;
  }

  .subtitle {
    font-size: 12px;
  }

  .reset-password-form {
    gap: 12px;
    margin-bottom: 12px;
  }

  .password-input-wrapper input {
    padding: 8px 40px 8px 10px;
    font-size: 12px;
  }

  .btn {
    padding: 8px 16px;
    font-size: 13px;
  }

  .success-icon-container,
  .error-icon-container {
    width: 60px;
    height: 60px;
  }

  .success-checkmark,
  .error-x {
    font-size: 36px;
  }

  .password-requirements {
    padding: 10px;
    margin-top: 6px;
  }

  .requirements-grid {
    gap: 6px;
  }

  .requirement {
    font-size: 11px;
  }

  .footer-info {
    margin-top: 16px;
    padding-top: 12px;
  }

  .footer-info p {
    font-size: 11px;
  }
}
</style>
